import React from 'react';
import styled from 'styled-components';
import { FaEnvelope } from 'react-icons/fa';

// Styled Components
const PageHeader = styled.div`
  background-color: #eaf4ff;
  padding: 4rem 2rem;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: #555;
  max-width: 600px;
  margin: 0.5rem auto 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const CenteredContentWrapper = styled(ContentWrapper)`
  justify-content: center;
`;

const FormContainer = styled.div`
  flex: 2;
  min-width: 300px;
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
`;

const InfoContainer = styled.div`
  flex: 1;
  min-width: 300px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #444;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
`;

const Textarea = styled.textarea`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  min-height: 150px;
  resize: vertical;
`;

const SubmitButton = styled.button`
  padding: 0.8rem 2rem;
  background-color: #2a7de1;
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  align-self: flex-start;

  &:hover {
    background-color: #1a6ac4;
  }
`;

const InfoBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  
  svg {
    font-size: 1.5rem;
    color: #2a7de1;
  }
`;


const Contact = () => {
  const [status, setStatus] = React.useState({ loading: false, success: false, error: null });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Phone validation
    const phoneRegex = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
    if (data.phone && !phoneRegex.test(data.phone)) {
      setStatus({
        loading: false,
        success: false,
        error: "Please enter a valid phone number (e.g., 123-456-7890)"
      });
      return;
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit form');
      }

      setStatus({ loading: false, success: true, error: null });
      e.target.reset(); // Clear the form
    } catch (err) {
      console.error('Submission error:', err);
      setStatus({ loading: false, success: false, error: err.message });
    }
  };

  if (status.success) {
    return (
      <>
        <PageHeader>
          <PageTitle>Thank you!</PageTitle>
          <PageSubtitle>Your message has been sent successfully. We will get back to you soon.</PageSubtitle>
        </PageHeader>
        <CenteredContentWrapper>
          <SubmitButton onClick={() => setStatus({ success: false })}>Send Another Message</SubmitButton>
        </CenteredContentWrapper>
      </>
    );
  }

  return (
    <>
      <PageHeader>
        <PageTitle>Get in Touch</PageTitle>
        <PageSubtitle>
          We're here to help and answer any questions you might have. We look forward to hearing from you.
        </PageSubtitle>
      </PageHeader>

      <ContentWrapper>
        <FormContainer>
          <h2>Send Us a Message</h2>
          <StyledForm onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="name">Full Name</Label>
              <Input type="text" id="name" name="name" required />
            </InputGroup>
            <InputGroup>
              <Label htmlFor="email">Email Address</Label>
              <Input type="email" id="email" name="email" required />
            </InputGroup>
            <InputGroup>
              <Label htmlFor="phone">Phone Number</Label>
              <Input type="tel" id="phone" name="phone" />
            </InputGroup>
            <InputGroup>
              <Label htmlFor="message">Your Message</Label>
              <Textarea id="message" name="message" required></Textarea>
            </InputGroup>
            {status.error && (
              <div style={{ color: 'red', marginBottom: '1rem' }}>
                {status.error}
              </div>
            )}
            <SubmitButton type="submit">Submit</SubmitButton>
          </StyledForm>
        </FormContainer>

        <InfoContainer>
          <h2>Contact Information</h2>
          <InfoBlock>
            <FaEnvelope />
            <span>info@befriendtherapy.com</span>
          </InfoBlock>
        </InfoContainer>
      </ContentWrapper>
    </>
  );
};

export default Contact;